import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card } from "react-bootstrap";
import {
  FaWalking,
  FaTemperatureHigh,
  FaHeart,
  FaLungs,
  FaBatteryHalf,
  FaShoePrints,
} from "react-icons/fa";
import "./UserCSS/custom.css";
import ActivityTrackCard from "./activitytrackcard";

import { fetchLatestTemperature, selectTemperature } from "../../redux/slices/temperatureSlice";
import { fetchLatestHeartRate, selectHeartRate } from "../../redux/slices/heartRateSlice";
import { fetchLatestSpO2, selectSpO2 } from "../../redux/slices/spo2Slice";
import { fetchLatestBattery, selectBattery } from "../../redux/slices/batterySlice";
import { fetchLatestSteps, selectSteps } from "../../redux/slices/stepsSlice";
import { fetchPatientDetails, selectPatientDetails } from "../../redux/slices/PatientSlice";

function DashboardCards({ patientId, setActiveChart }) {
  const dispatch = useDispatch();

  const temperatureState = useSelector(selectTemperature);
  const heartRateState = useSelector(selectHeartRate);
  const spo2State = useSelector(selectSpO2);
  const batteryState = useSelector(selectBattery);
  const stepsState = useSelector(selectSteps);
  const { info: patient, loading: patientInfoLoading, error: patientInfoError } = useSelector(selectPatientDetails);

  useEffect(() => {
    if (!patientId) return;

    // Initial fetch
    dispatch(fetchPatientDetails(patientId));
    dispatch(fetchLatestTemperature(patientId));
    dispatch(fetchLatestHeartRate(patientId));
    dispatch(fetchLatestSpO2(patientId));
    dispatch(fetchLatestBattery(patientId));
    dispatch(fetchLatestSteps(patientId));

    // Polling every 10 seconds
    const interval = setInterval(() => {
      dispatch(fetchLatestTemperature(patientId));
      dispatch(fetchLatestHeartRate(patientId));
      dispatch(fetchLatestSpO2(patientId));
      dispatch(fetchLatestBattery(patientId));
      dispatch(fetchLatestSteps(patientId));
    }, 10000);

    return () => clearInterval(interval);
  }, [patientId, dispatch]);

  return (
    <div className="card-container">
      <div className="container mt-4">
        {patientInfoLoading ? (
          <div className="alert alert-info">Loading patient info...</div>
        ) : patientInfoError ? (
          <div className="alert alert-danger">{patientInfoError}</div>
        ) : patient ? (
          <div className="patient-banner d-flex flex-wrap gap-3 mb-4 p-3 justify-content-between text-dark">
            <div><strong>Patient Name:</strong> {patient.patient_name}</div>
            <div><strong>Patient ID:</strong> {patient.patient_id}</div>
            <div><strong>Gender:</strong> {patient.gender}</div>
            <div><strong>Age:</strong> {patient.age}</div>
          </div>
        ) : (
          <div className="alert alert-warning">No patient assigned</div>
        )}
      </div>

      <Row className="d-flex justify-content-between px-2">

        {/* Activity Track
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-activity-track" onClick={() => setActiveChart("activitytrack")}>
            <Card.Header><FaWalking className="icon" /> Activity Track</Card.Header>
            <Card.Body>
              <ActivityTrackCard patientId={patientId} className="card-value" showHeader={false} />
            </Card.Body>
          </Card>
        </Col> */}

        {/* Temperature */}
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-temperature" onClick={() => setActiveChart("temperature")}>
            <Card.Header><FaTemperatureHigh className="icon" /> Temperature</Card.Header>
            <Card.Body>
              <Card.Text className="card-value">
                {temperatureState.loading
                  ? "Loading..."
                  : temperatureState.data?.temperature != null
                  ? `${temperatureState.data.temperature} °C`
                  : "—"}
              </Card.Text>
              <Card.Text className="small text-muted">
                {temperatureState.data?.time ? "Last updated: " + new Date(temperatureState.data.time).toLocaleString() : ""}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* Heart Rate */}
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-heart-rate" onClick={() => setActiveChart("heartrate")}>
            <Card.Header><FaHeart className="icon" /> Heart Rate</Card.Header>
            <Card.Body>
              <Card.Text className="card-value">
                {heartRateState.loading
                  ? "Loading..."
                  : heartRateState.data?.heart_rate != null
                  ? `${heartRateState.data.heart_rate} bpm`
                  : "—"}
              </Card.Text>
              <Card.Text className="small text-muted">
                {heartRateState.data?.time ? "Last updated: " + new Date(heartRateState.data.time).toLocaleString() : ""}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* Oxygen Level */}
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-oximeter" onClick={() => setActiveChart("oximeter")}>
            <Card.Header><FaLungs className="icon" /> Oxygen Level</Card.Header>
            <Card.Body>
              <Card.Text className="card-value">
                {spo2State.loading
                  ? "Loading..."
                  : spo2State.data?.spo2 != null
                  ? `${spo2State.data.spo2}%`
                  : "—"}
              </Card.Text>
              <Card.Text className="small text-muted">
                {spo2State.data?.time ? "Last updated: " + new Date(spo2State.data.time).toLocaleString() : ""}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* Steps Count */}
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-steps" onClick={() => setActiveChart("steps")}>
            <Card.Header><FaShoePrints className="icon" /> Steps Count</Card.Header>
            <Card.Body>
              <Card.Text className="card-value">
                {stepsState.loading
                  ? "Loading..."
                  : stepsState.data?.steps_count != null
                  ? `${stepsState.data.steps_count} steps`
                  : "—"}
              </Card.Text>
              <Card.Text className="small text-muted">
                {stepsState.data?.time ? "Last updated: " + new Date(stepsState.data.time).toLocaleString() : ""}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* Battery Level */}
        <Col xs={12} sm={6} md={4} className="mb-4">
          <Card className="card-battery" onClick={() => setActiveChart("battery")}>
            <Card.Header><FaBatteryHalf className="icon" /> Battery Level</Card.Header>
            <Card.Body>
              <Card.Text className="card-value">
                {batteryState.loading
                  ? "Loading..."
                  : batteryState.data?.battery_level != null
                  ? `${batteryState.data.battery_level}%`
                  : "—"}
              </Card.Text>
              <Card.Text className="small text-muted">
                {batteryState.data?.time ? "Last updated: " + new Date(batteryState.data.time).toLocaleString() : ""}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardCards;
